import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Sun, Moon, Rocket, ShoppingCart, LayoutDashboard, LogIn, Gauge } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const links = [
  { to: "/ai-business-growth", label: "Growth Report" },
  { to: "/template-recommender", label: "Find My Template" },
  { to: "/solutions", label: "Solutions" },
  { to: "/resources", label: "Resources" },
  { to: "/seo-tools", label: "SEO Tools" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
];

export const Navbar = ({ sticky = true }) => {
  const { theme, toggle } = useTheme();
  const { count } = useCart();
  const { user, login } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  return (
    <header className={`${sticky ? "sticky top-0" : "relative"} z-50 glass border-b`} data-testid="navbar">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          <span className="h-9 w-9 rounded-xl bg-primary text-primary-foreground grid place-items-center font-head font-black">SA</span>
          <span className="font-head font-extrabold text-base sm:text-lg leading-none hidden sm:block">
            San Antonio's<span className="block text-xs font-medium text-muted-foreground">Web Design</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:text-primary ${isActive ? "text-primary" : "text-foreground/80"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button data-testid="theme-toggle" onClick={toggle} aria-label="Toggle theme"
            className="h-10 w-10 grid place-items-center rounded-full glass hover:text-primary transition-colors">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Link to="/cart" data-testid="nav-cart" aria-label="Cart"
            className="relative h-10 w-10 grid place-items-center rounded-full glass hover:text-primary transition-colors">
            <ShoppingCart className="h-4 w-4" />
            {count > 0 && <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground text-xs font-bold grid place-items-center">{count}</span>}
          </Link>
          <Link to="/get-started" data-testid="order-now-nav"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 h-10 text-sm font-semibold hover:brightness-110 transition-[filter] shadow-lg shadow-primary/30">
            <Rocket className="h-4 w-4" /> Order Now
          </Link>
          {user?.is_admin && (
            <Link to="/admin" data-testid="nav-admin" aria-label="Admin Hub"
              className="hidden sm:grid h-10 w-10 place-items-center rounded-full glass hover:text-primary transition-colors">
              <Gauge className="h-4 w-4" />
            </Link>
          )}
          {user ? (
            <Link to="/dashboard" data-testid="nav-dashboard" aria-label="Dashboard"
              className="h-10 w-10 grid place-items-center rounded-full glass hover:text-primary transition-colors overflow-hidden">
              {user.picture ? <img src={user.picture} alt={user.name} className="h-10 w-10 object-cover" /> : <LayoutDashboard className="h-4 w-4" />}
            </Link>
          ) : (
            <button onClick={login} data-testid="nav-signin"
              className="hidden sm:inline-flex items-center gap-2 rounded-full glass px-4 h-10 text-sm font-semibold hover:text-primary transition-colors">
              <LogIn className="h-4 w-4" /> Sign in
            </button>
          )}
          <button data-testid="mobile-menu-toggle" className="lg:hidden h-10 w-10 grid place-items-center rounded-full glass"
            onClick={() => setOpen((o) => !o)} aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border px-4 py-3 space-y-1 glass max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          {[...links, { to: "/pricing", label: "Pricing" }].map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block px-3 py-2 rounded-lg font-medium hover:bg-secondary">
              {l.label}
            </NavLink>
          ))}
          <button onClick={() => { setOpen(false); nav("/get-started"); }}
            className="w-full mt-2 rounded-full bg-primary text-primary-foreground py-2.5 font-semibold">
            Order Now
          </button>
          {user ? (
            <button onClick={() => { setOpen(false); nav("/dashboard"); }} data-testid="mobile-dashboard"
              className="w-full mt-2 rounded-full glass py-2.5 font-semibold inline-flex items-center justify-center gap-2">
              <LayoutDashboard className="h-4 w-4" /> My Dashboard
            </button>
          ) : (
            <button onClick={() => { setOpen(false); login(); }} data-testid="mobile-signin"
              className="w-full mt-2 rounded-full glass py-2.5 font-semibold inline-flex items-center justify-center gap-2">
              <LogIn className="h-4 w-4" /> Sign in with Google
            </button>
          )}
        </div>
      )}
    </header>
  );
};
